import * as THREE from 'three/webgpu';
import { hashSeed, Rng } from '../math/rng';
import type { TreeShape } from './VegetationStyle';

/** A piece of a tree: its geometry, which part it is (0 wood, 1 crown, 2 glow organ), smooth normals or not. */
export interface TreePart {
  geo: THREE.BufferGeometry;
  part: number;
  smooth?: boolean;
}

/**
 * A generated tree, at scale 1, standing on y = 0: its parts, and its crown's underside height
 * and radius (m) for the canopy shade map (null: no crown to shade the ground).
 */
export interface TreeTemplate {
  parts: TreePart[];
  crown: { bottom: number; radius: number } | null;
}

/** Each shape's own stream, so a species looks nothing like another drawn from the same seed. */
const SHAPE_SALT: Record<TreeShape, number> = {
  conifer: 1,
  broadleaf: 2,
  mushroom: 3,
  lantern: 4,
  crystal: 5,
  boulder: 6,
  palm: 7,
  cactus: 8,
  acacia: 9,
  dead: 10,
  shrub: 11,
};

/** Bark sides: trunks are six-sided (their normals are smoothed), branches five. */
const TRUNK_SIDES = 6;
const BRANCH_SIDES = 5;
/** How far a leafy clump's corners move in or out, as a share of its radius. */
const LUMP = 0.16;

const _t = new THREE.Vector3();
const _n = new THREE.Vector3();
const _b = new THREE.Vector3();
const _prev = new THREE.Vector3();

/**
 * A tree of `shape`, generated from `seed`: the same seed gives the same tree on every machine
 * (seeded integer random numbers and plain arithmetic), so everyone who plays a world sees the
 * same trees. Lightweight: a few hundred corners, built once per species when a world loads.
 * The renderer gives every tree it places its own height, width and lean on top of this.
 */
export function generateTree(shape: TreeShape, seed: number): TreeTemplate {
  const rng = new Rng(hashSeed(seed >>> 0, SHAPE_SALT[shape]));
  switch (shape) {
    case 'broadleaf':
      return broadleaf(rng);
    case 'conifer':
      return conifer(rng);
    case 'acacia':
      return acacia(rng);
    case 'dead':
      return dead(rng);
    case 'palm':
      return palm(rng);
    case 'shrub':
      return shrub(rng);
    case 'mushroom':
      return mushroom(rng);
    case 'cactus':
      return cactus(rng);
    case 'crystal':
      return crystal(rng);
    case 'boulder':
      return boulder(rng);
    default:
      return lantern();
  }
}

/**
 * A tube along `points`, `radii[i]` round there, with `sides` sides and open ends: bark for
 * trunks and branches. Its rings follow the path without twisting (parallel transport), wound
 * so its faces look outward.
 */
export function tube(points: THREE.Vector3[], radii: number[], sides: number): THREE.BufferGeometry {
  const n = points.length;
  const pos = new Float32Array(n * sides * 3);
  const idx: number[] = [];
  // A first normal across the path's start, then carried along it.
  _t.subVectors(points[1], points[0]).normalize();
  _n.set(Math.abs(_t.y) < 0.9 ? 0 : 1, Math.abs(_t.y) < 0.9 ? 1 : 0, 0);
  _n.addScaledVector(_t, -_n.dot(_t)).normalize();
  for (let i = 0; i < n; i++) {
    const a = points[Math.max(0, i - 1)];
    const c = points[Math.min(n - 1, i + 1)];
    _t.subVectors(c, a).normalize();
    _prev.copy(_n);
    _n.copy(_prev).addScaledVector(_t, -_prev.dot(_t)).normalize();
    _b.crossVectors(_t, _n);
    for (let j = 0; j < sides; j++) {
      const th = (j / sides) * Math.PI * 2;
      const cs = Math.cos(th) * radii[i];
      const sn = Math.sin(th) * radii[i];
      const o = (i * sides + j) * 3;
      pos[o] = points[i].x + _n.x * cs + _b.x * sn;
      pos[o + 1] = points[i].y + _n.y * cs + _b.y * sn;
      pos[o + 2] = points[i].z + _n.z * cs + _b.z * sn;
    }
  }
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < sides; j++) {
      const a = i * sides + j;
      const b = i * sides + ((j + 1) % sides);
      const c = (i + 1) * sides + j;
      const d = (i + 1) * sides + ((j + 1) % sides);
      idx.push(a, b, c, b, d, c);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(idx);
  return g;
}

/**
 * A leafy clump with a ragged outline: each corner pushed in or out (up to LUMP of its radius)
 * by a smooth function of its direction, so the copies of a corner that its faces share move
 * together (and are joined again when the tree is put together).
 */
export function lumpy(geo: THREE.BufferGeometry, seed: number): THREE.BufferGeometry {
  const pos = geo.getAttribute('position');
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const r = Math.hypot(x, y, z);
    const k =
      1 +
      LUMP *
        Math.sin((x * 3.1 + y * 2.3) / r + seed) *
        Math.cos((z * 2.7 - x * 1.9 + y * 1.3) / r + seed * 1.7);
    pos.setXYZ(i, x * k, y * k, z * k);
  }
  return geo;
}

/** A point along a quadratic bend from a through b (pulled toward `mid`) at t. */
function bend(a: THREE.Vector3, mid: THREE.Vector3, b: THREE.Vector3, t: number): THREE.Vector3 {
  const u = 1 - t;
  return new THREE.Vector3(
    u * u * a.x + 2 * u * t * mid.x + t * t * b.x,
    u * u * a.y + 2 * u * t * mid.y + t * t * b.y,
    u * u * a.z + 2 * u * t * mid.z + t * t * b.z,
  );
}

/** A limb from `a` to `b`, bowed toward `mid`, `segments` long, tapering from r0 to r1. */
function limb(
  a: THREE.Vector3,
  mid: THREE.Vector3,
  b: THREE.Vector3,
  r0: number,
  r1: number,
  segments: number,
  sides: number,
): THREE.BufferGeometry {
  const pts: THREE.Vector3[] = [];
  const radii: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    pts.push(bend(a, mid, b, t));
    radii.push(r0 + (r1 - r0) * t);
  }
  return tube(pts, radii, sides);
}

/** A trunk rising `height` m from the ground, swaying off true by up to `wander` m at its top. */
function trunkPath(rng: Rng, height: number, wander: number, segments: number): THREE.Vector3[] {
  const pts: THREE.Vector3[] = [];
  const dx = rng.range(-wander, wander);
  const dz = rng.range(-wander, wander);
  // A little below the ground, so its foot never shows a gap on a slope.
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const wobble = Math.sin(t * Math.PI) * 0.12;
    pts.push(
      new THREE.Vector3(dx * t * t + wobble * dz, -0.3 + (height + 0.3) * t, dz * t * t - wobble * dx),
    );
  }
  return pts;
}

/** Radii down a trunk: `foot` at the ground to `top`, flaring at the very foot. */
function trunkRadii(count: number, foot: number, top: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    out.push(top + (foot - top) * (1 - t) ** 1.4 + (i === 0 ? foot * 0.25 : 0));
  }
  return out;
}

/** A direction out from the trunk: round it at `azimuth`, up from level by `elevation` (radians). */
function outward(azimuth: number, elevation: number): THREE.Vector3 {
  return new THREE.Vector3(
    Math.cos(azimuth) * Math.cos(elevation),
    Math.sin(elevation),
    Math.sin(azimuth) * Math.cos(elevation),
  );
}

/** The crown's underside and radius from its clumps (centre, radius each), for the shade map. */
function crownOf(clumps: { c: THREE.Vector3; r: number }[]): { bottom: number; radius: number } {
  let bottom = Number.POSITIVE_INFINITY;
  let radius = 0;
  for (const k of clumps) {
    bottom = Math.min(bottom, k.c.y - k.r * 0.6);
    radius = Math.max(radius, Math.hypot(k.c.x, k.c.z) + k.r * 0.85);
  }
  return { bottom, radius };
}

/** A broadleaf: a bending trunk forking into a few limbs, each ending in a clump of leaves, with one more on top. */
function broadleaf(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const height = rng.range(2.7, 3.7);
  const path = trunkPath(rng, height, 0.35, 4);
  parts.push({ geo: tube(path, trunkRadii(path.length, 0.3, 0.17), TRUNK_SIDES), part: 0, smooth: true });
  const top = path[path.length - 1];
  const spread = rng.range(2.3, 3.1);
  const lift = rng.range(1.5, 2.3);
  const centre = top.clone().add(new THREE.Vector3(0, lift, 0));
  const clumps: { c: THREE.Vector3; r: number }[] = [
    {
      c: centre
        .clone()
        .add(new THREE.Vector3(rng.range(-0.3, 0.3), rng.range(0.4, 0.9), rng.range(-0.3, 0.3))),
      r: rng.range(2.1, 2.6),
    },
  ];
  const limbs = rng.int(3, 5);
  const turn = rng.range(0, Math.PI * 2);
  for (let i = 0; i < limbs; i++) {
    const az = turn + (i / limbs) * Math.PI * 2 + rng.range(-0.35, 0.35);
    const from = bend(path[1], path[2], top, rng.range(0.55, 0.95));
    const reach = spread * rng.range(0.6, 0.85);
    const dir = outward(az, rng.range(0.35, 0.75));
    const end = centre
      .clone()
      .addScaledVector(dir, reach)
      .add(new THREE.Vector3(0, rng.range(-0.9, 0.2), 0));
    const mid = from
      .clone()
      .lerp(end, 0.5)
      .add(new THREE.Vector3(0, rng.range(0.2, 0.6), 0));
    parts.push({ geo: limb(from, mid, end, 0.13, 0.05, 3, BRANCH_SIDES), part: 0, smooth: true });
    clumps.push({ c: end.clone().add(new THREE.Vector3(0, 0.35, 0)), r: rng.range(1.35, 1.9) });
  }
  clumps.forEach((k, i) => {
    const g = lumpy(new THREE.IcosahedronGeometry(k.r, 1), rng.range(0, 9) + i);
    g.translate(k.c.x, k.c.y, k.c.z);
    parts.push({ geo: g, part: 1 });
  });
  return { parts, crown: crownOf(clumps) };
}

/** A conifer: a straight trunk through tiers of drooping, ragged boughs, narrowing to the tip. */
function conifer(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const tiers = rng.int(4, 6);
  const base = rng.range(1.6, 2.4);
  const height = rng.range(9, 12.5);
  const path = trunkPath(rng, height * 0.55, 0.12, 3);
  parts.push({ geo: tube(path, trunkRadii(path.length, 0.28, 0.13), TRUNK_SIDES), part: 0, smooth: true });
  const wide = rng.range(2.2, 2.9);
  let bottom = Number.POSITIVE_INFINITY;
  let radius = 0;
  for (let i = 0; i < tiers; i++) {
    const t = i / (tiers - 1);
    const y = base + (height - base - 2.2) * t ** 0.9;
    const r = wide * (1 - 0.72 * t) * rng.range(0.9, 1.08);
    const h = r * rng.range(1.35, 1.75);
    const cone = new THREE.ConeGeometry(r, h, 9, 1, true);
    // A ragged rim: boughs of different lengths (a smooth function of the angle, so the cone's
    // seam moves as one).
    const phase = rng.range(0, Math.PI * 2);
    const ragged = rng.range(0.1, 0.2);
    const pos = cone.getAttribute('position');
    for (let k = 0; k < pos.count; k++) {
      if (pos.getY(k) > -h / 2 + 1e-4) continue;
      const a = Math.atan2(pos.getZ(k), pos.getX(k));
      const s = 1 + ragged * Math.sin(a * 3 + phase) * Math.cos(a * 2 - phase * 0.7);
      pos.setXYZ(k, pos.getX(k) * s, pos.getY(k) - ragged * 0.6 * (s - 1) * h, pos.getZ(k) * s);
    }
    cone.rotateX(rng.range(-0.07, 0.07));
    cone.rotateZ(rng.range(-0.07, 0.07));
    cone.translate(rng.range(-0.12, 0.12), y + h / 2, rng.range(-0.12, 0.12));
    parts.push({ geo: cone, part: 1 });
    bottom = Math.min(bottom, y);
    radius = Math.max(radius, r * 0.95);
  }
  return { parts, crown: { bottom, radius } };
}

/** An acacia: a short trunk forking into two or three limbs that hold up a wide, flat crown. */
function acacia(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const fork = rng.range(2.2, 3.2);
  const path = trunkPath(rng, fork, 0.3, 3);
  parts.push({ geo: tube(path, trunkRadii(path.length, 0.27, 0.16), TRUNK_SIDES), part: 0, smooth: true });
  const top = path[path.length - 1];
  const height = fork + rng.range(1.5, 2.2);
  const limbs = rng.int(2, 3);
  const turn = rng.range(0, Math.PI * 2);
  const clumps: { c: THREE.Vector3; r: number }[] = [];
  for (let i = 0; i < limbs; i++) {
    const az = turn + (i / limbs) * Math.PI * 2 + rng.range(-0.4, 0.4);
    const out = rng.range(1.1, 1.9);
    const end = new THREE.Vector3(
      top.x + Math.cos(az) * out,
      height + rng.range(-0.3, 0.2),
      top.z + Math.sin(az) * out,
    );
    const mid = top
      .clone()
      .lerp(end, 0.5)
      .add(new THREE.Vector3(0, -0.1, 0));
    parts.push({ geo: limb(top, mid, end, 0.13, 0.06, 3, BRANCH_SIDES), part: 0, smooth: true });
    clumps.push({ c: end.clone().add(new THREE.Vector3(0, 0.25, 0)), r: rng.range(1.6, 2.2) });
  }
  clumps.push({ c: new THREE.Vector3(top.x, height + 0.45, top.z), r: rng.range(2.2, 2.8) });
  for (const [i, k] of clumps.entries()) {
    const g = lumpy(new THREE.IcosahedronGeometry(1, 1), rng.range(0, 9) + i);
    // Flat: wide pads of leaves.
    g.scale(k.r * rng.range(1.05, 1.25), k.r * rng.range(0.26, 0.34), k.r);
    g.translate(k.c.x, k.c.y, k.c.z);
    parts.push({ geo: g, part: 1 });
  }
  const crown = crownOf(clumps);
  return { parts, crown: { bottom: height - 0.2, radius: crown.radius } };
}

/** A dead tree: a bare, bending trunk and crooked branches, some forking again. No leaves. */
function dead(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const height = rng.range(4.5, 6.5);
  const path = trunkPath(rng, height, 0.5, 4);
  parts.push({ geo: tube(path, trunkRadii(path.length, 0.3, 0.08), TRUNK_SIDES), part: 0, smooth: true });
  const branches = rng.int(4, 7);
  for (let i = 0; i < branches; i++) {
    const at = rng.range(0.35, 0.92);
    const seg = Math.min(path.length - 2, Math.floor(at * (path.length - 1)));
    const from = path[seg].clone().lerp(path[seg + 1], at * (path.length - 1) - seg);
    const az = rng.range(0, Math.PI * 2);
    const len = rng.range(1.0, 2.0) * (1.2 - at * 0.5);
    const end = from.clone().addScaledVector(outward(az, rng.range(0.25, 0.8)), len);
    const mid = from
      .clone()
      .lerp(end, 0.5)
      .add(new THREE.Vector3(rng.range(-0.25, 0.25), rng.range(-0.2, 0.3), rng.range(-0.25, 0.25)));
    parts.push({ geo: limb(from, mid, end, 0.08, 0.025, 2, BRANCH_SIDES), part: 0, smooth: true });
    if (rng.chance(0.5)) {
      const twig = end
        .clone()
        .addScaledVector(outward(az + rng.range(-1, 1), rng.range(0.4, 1.0)), len * 0.5);
      parts.push({
        geo: limb(mid, mid.clone().lerp(twig, 0.5), twig, 0.04, 0.015, 2, 4),
        part: 0,
        smooth: true,
      });
    }
  }
  return { parts, crown: null };
}

/** A palm: a slender trunk curving as it rises, and a crown of long drooping fronds. */
function palm(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const segments = 6;
  const az = rng.range(0, Math.PI * 2);
  const curve = rng.range(0.02, 0.06);
  const pts: THREE.Vector3[] = [];
  const radii: number[] = [];
  let x = 0;
  let y = -0.3;
  for (let k = 0; k <= segments; k++) {
    pts.push(new THREE.Vector3(Math.cos(az) * x, y, Math.sin(az) * x));
    radii.push(0.25 - 0.018 * k);
    const lean = 0.04 + curve * k;
    x += Math.sin(lean) * 1.3;
    y += Math.cos(lean) * 1.3;
  }
  parts.push({ geo: tube(pts, radii, TRUNK_SIDES), part: 0, smooth: true });
  const top = pts[pts.length - 1];
  const fronds = rng.int(7, 10);
  let reach = 0;
  for (let i = 0; i < fronds; i++) {
    const len = rng.range(3.0, 4.2);
    const frond = new THREE.ConeGeometry(0.5, len, 4, 1, true);
    frond.scale(1, 1, 0.22);
    frond.translate(0, len / 2, 0);
    const droop = Math.PI / 2 + rng.range(0.1, 0.45);
    frond.rotateZ(-droop);
    frond.rotateY((i / fronds) * Math.PI * 2 + rng.range(-0.25, 0.25));
    frond.translate(top.x, top.y, top.z);
    parts.push({ geo: frond, part: 1 });
    reach = Math.max(reach, len * Math.sin(droop) * 0.75);
  }
  return { parts, crown: { bottom: top.y - 0.9, radius: reach } };
}

/** A low shrub: a few round clumps close to the ground. */
function shrub(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const clumps = rng.int(3, 5);
  for (let i = 0; i < clumps; i++) {
    const r = rng.range(0.45, 0.85);
    const a = rng.range(0, Math.PI * 2);
    const d = i === 0 ? 0 : rng.range(0.35, 0.7);
    const g = lumpy(new THREE.IcosahedronGeometry(r, 1), rng.range(0, 9) + i);
    g.translate(Math.cos(a) * d, r * 0.7, Math.sin(a) * d);
    parts.push({ geo: g, part: 1 });
  }
  return { parts, crown: null };
}

/** A giant mushroom: a stalk, a domed cap, glowing gills underneath. */
function mushroom(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const height = 5.7 * rng.range(0.9, 1.1);
  const capR = 3.6 * rng.range(0.92, 1.06);
  const stalk = new THREE.CylinderGeometry(0.45, 0.75, height, 10, 1, false);
  stalk.translate(0, height / 2 - 0.1, 0);
  parts.push({ geo: stalk, part: 0, smooth: true });
  const cap = new THREE.SphereGeometry(capR, 18, 8, 0, Math.PI * 2, 0, Math.PI / 2);
  cap.scale(1, rng.range(0.42, 0.58), 1);
  cap.translate(0, height - 0.1, 0);
  parts.push({ geo: cap, part: 1, smooth: true });
  const gills = new THREE.CircleGeometry(capR * 0.986, 18);
  gills.rotateX(Math.PI / 2);
  gills.translate(0, height - 0.1, 0);
  parts.push({ geo: gills, part: 2 });
  return { parts, crown: { bottom: height - 0.4, radius: capR * 0.94 } };
}

/** A saguaro: a ribbed column with arms, all green. */
function cactus(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const height = rng.range(3.4, 4.8);
  const column = new THREE.CylinderGeometry(0.34, 0.4, height, 10, 1, true);
  column.translate(0, height / 2, 0);
  parts.push({ geo: column, part: 1 });
  const top = new THREE.SphereGeometry(0.34, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2);
  top.translate(0, height, 0);
  parts.push({ geo: top, part: 1, smooth: true });
  const arms = rng.int(0, 3);
  const turn = rng.range(0, Math.PI * 2);
  for (let i = 0; i < arms; i++) {
    const az = turn + i * ((Math.PI * 2) / Math.max(arms, 2)) + rng.range(-0.4, 0.4);
    const at = rng.range(1.5, height * 0.62);
    const rise = rng.range(0.9, Math.min(1.8, height - at - 0.2));
    const out = rng.range(0.85, 1.05);
    const elbow = new THREE.CylinderGeometry(0.24, 0.24, out - 0.3, 8, 1, true);
    elbow.rotateZ(Math.PI / 2);
    elbow.translate((out - 0.3) / 2 + 0.3, at, 0);
    elbow.rotateY(-az);
    parts.push({ geo: elbow, part: 1 });
    const arm = new THREE.CylinderGeometry(0.22, 0.24, rise, 8, 1, true);
    arm.translate(out, at + rise / 2, 0);
    arm.rotateY(-az);
    parts.push({ geo: arm, part: 1 });
    const cap = new THREE.SphereGeometry(0.22, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2);
    cap.translate(out, at + rise, 0);
    cap.rotateY(-az);
    parts.push({ geo: cap, part: 1, smooth: true });
  }
  return { parts, crown: null };
}

/** A crystal cluster: a rough stone base with glowing spires leaning outward. */
function crystal(rng: Rng): TreeTemplate {
  const parts: TreePart[] = [];
  const base = new THREE.IcosahedronGeometry(1.1, 0);
  base.scale(1, 0.45, 1);
  parts.push({ geo: base, part: 0 });
  const spires = rng.int(3, 6);
  const turn = rng.range(0, Math.PI * 2);
  for (let i = 0; i < spires; i++) {
    const centre = i === 0;
    const h = centre ? rng.range(3.6, 4.6) : rng.range(1.8, 3.2);
    const r = centre ? rng.range(0.4, 0.5) : rng.range(0.28, 0.38);
    const az = turn + (i / spires) * Math.PI * 2 + rng.range(-0.3, 0.3);
    const d = centre ? 0 : rng.range(0.4, 0.7);
    const tilt = centre ? rng.range(0, 0.08) : rng.range(0.2, 0.45);
    const g = new THREE.OctahedronGeometry(1, 0);
    g.scale(r, h / 2, r);
    g.translate(0, h / 2, 0);
    g.rotateZ(-tilt);
    g.rotateY(-az);
    g.translate(Math.cos(az) * d, 0.1, Math.sin(az) * d);
    parts.push({ geo: g, part: 2 });
  }
  return { parts, crown: null };
}

/** A lumpy boulder: a displaced icosahedron, flattened and half sunk into the ground. */
function boulder(rng: Rng): TreeTemplate {
  const g = new THREE.IcosahedronGeometry(1, 1);
  const pos = g.getAttribute('position');
  const a = rng.range(4, 6);
  const b = rng.range(3, 4.5);
  const c = rng.range(3.5, 5);
  const p = rng.range(0, Math.PI * 2);
  const flat = rng.range(0.6, 0.8);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const k = 1 + 0.22 * Math.sin(x * a + y * b + p) * Math.cos(z * c - x * 2.1);
    pos.setXYZ(i, x * k * 1.2, y * k * flat + 0.35, z * k);
  }
  return { parts: [{ geo: g, part: 1 }], crown: null };
}

/**
 * A lantern stalk: a thin stem with a hooded, glowing bulb at the top. The same every time:
 * its light (VegetationStyle.light) sits at the bulb.
 */
function lantern(): TreeTemplate {
  const stem = new THREE.CylinderGeometry(0.08, 0.2, 6.5, 6, 1, true);
  stem.translate(0, 3.25, 0);
  const bulb = new THREE.IcosahedronGeometry(0.75, 2);
  bulb.scale(1, 1.35, 1);
  bulb.translate(0, 6.9, 0);
  const hood = new THREE.ConeGeometry(1.0, 1.4, 8, 1, true);
  hood.translate(0, 8.0, 0);
  return {
    parts: [
      { geo: stem, part: 0, smooth: true },
      { geo: bulb, part: 2 },
      { geo: hood, part: 1 },
    ],
    crown: null,
  };
}
